import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationCenter({ token }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const headers = useCallback(() => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/`, { headers: headers() });
      const data = await res.json();
      if (data.success) setNotifications(data.notifications || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  const markAsRead = async (id) => {
    try {
      await fetch(`${API_URL}/api/notifications/${id}/read`, { method: 'PUT', headers: headers() });
      setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, is_read: true } : n));
    } catch (err) { console.error(err); }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/read-all`, { method: 'PUT', headers: headers() });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) { console.error(err); }
  };

  const filtered = filter === 'all' ? notifications : filter === 'unread' ? notifications.filter(n => !n.is_read) : notifications.filter(n => n.type === filter);
  const unreadCount = notifications.filter(n => !n.is_read).length;

  const typeIcons = {
    'lead': 'fa-user-plus text-blue-500', 'fee': 'fa-indian-rupee-sign text-green-500',
    'attendance': 'fa-calendar-check text-violet-500', 'exam': 'fa-file-lines text-amber-500',
    'transport': 'fa-bus text-cyan-500', 'system': 'fa-gear text-gray-500',
    'campaign': 'fa-rectangle-ad text-rose-500', 'whatsapp': 'fa-brands fa-whatsapp text-emerald-500',
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="notification-center">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{L('Notifications', 'सूचनाएं')}</h1>
          <p className="text-sm text-gray-500">{unreadCount} {L(`unread notification${unreadCount !== 1 ? 's' : ''}`, 'अपठित सूचनाएं')}</p>
        </div>
        <button onClick={markAllRead} disabled={unreadCount === 0}
          className="px-4 py-2 bg-indigo-500 text-white rounded-xl text-sm font-medium hover:bg-indigo-600 disabled:opacity-50 transition" data-testid="mark-all-read-btn">
          <i className="fa-solid fa-check-double mr-2"></i>{L('Mark All Read', 'सभी पढ़ा हुआ करें')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { id: 'all', label: L('All', 'सभी') }, { id: 'unread', label: `${L('Unread', 'अपठित')} (${unreadCount})` },
          { id: 'lead', label: L('Leads', 'लीड्स') }, { id: 'fee', label: L('Fees', 'शुल्क') },
          { id: 'attendance', label: L('Attendance', 'उपस्थिति') }, { id: 'campaign', label: L('Campaigns', 'कैम्पेन') },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${filter === f.id ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            data-testid={`filter-${f.id}`}>{f.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-16 text-center">
          <i className="fa-solid fa-bell-slash text-4xl text-gray-300 mb-3"></i>
          <p className="text-gray-500 font-medium">{L('No notifications', 'कोई notification नहीं है')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((n, i) => (
            <div key={n.notification_id || i} onClick={() => !n.is_read && markAsRead(n.notification_id)}
              className={`bg-white rounded-xl border p-4 flex items-start gap-4 cursor-pointer transition hover:shadow-sm ${!n.is_read ? 'border-indigo-200 bg-indigo-50/30' : 'border-gray-100'}`}
              data-testid={`notification-${n.notification_id || i}`}>
              <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center shrink-0">
                <i className={`fa-solid ${typeIcons[n.type] || 'fa-bell text-gray-400'}`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`text-sm ${!n.is_read ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>{n.title || n.message}</p>
                  {!n.is_read && <span className="w-2 h-2 bg-indigo-500 rounded-full shrink-0"></span>}
                </div>
                {n.message && n.title && <p className="text-xs text-gray-500 mt-0.5 truncate">{n.message}</p>}
                <p className="text-xs text-gray-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString('hi-IN') : ''}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
